import { useState } from "react";
import { Phone, Mail, Clock, MapPin, ShieldCheck, Star, Facebook, Instagram, Loader2, Check } from "lucide-react";
import { BUSINESS, CITIES, TRUST_BADGES } from "@/data/site";
import { usePageMeta, PageHero, SectionLabel, StarRating } from "@/components/common";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const field = "w-full bg-white border-b-2 border-zinc-300 focus:border-adn-red rounded-none px-0 py-3 outline-none transition-colors text-adn-charcoal";
const labelCls = "block text-sm font-semibold text-adn-charcoal mb-1";
const selectCls = field + " appearance-none cursor-pointer";

const SERVICE_OPTIONS = ["Stucco Repair", "Stucco Installation", "Stucco Remediation", "Interior Plaster", "Exterior Plaster", "Stucco Restoration", "Crack Repair", "Waterproofing", "Stucco Textures", "New Construction Stucco", "Other"];

export default function Contact() {
  usePageMeta("Request a Free Estimate", "Contact A.D.N Stucco Inc for a free stucco estimate in Central Florida. Call 407-487-0442 or fill out our quick form.");
  const [consent, setConsent] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadError("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/uploads`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      const uploaded = (data.uploaded || []).map((u) => ({ filename: u.filename, url: `${BACKEND_URL}${u.url}` }));
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setUploadError("Photo upload failed. Please try again or email your photos to us.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero label="Get In Touch" title="Request Your Free Estimate" subtitle="Whether you need stucco repair, new installation, waterproofing, or interior plaster work, our experienced team is ready to help. Fill out the form below and we'll contact you with a free, no-obligation estimate." />

      <section className="py-20 bg-adn-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          {/* FORM */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-zinc-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-10">
              <SectionLabel>Free Estimate Request</SectionLabel>
              <h2 className="text-3xl font-heading font-extrabold mt-4 mb-8">Tell us about your project</h2>

              <form action={BUSINESS.formspree} method="POST" encType="multipart/form-data" data-testid="estimate-form" className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div><label className={labelCls} htmlFor="fullname">Full Name *</label><input id="fullname" name="Full Name" type="text" required className={field} data-testid="input-fullname" /></div>
                  <div><label className={labelCls} htmlFor="email">Email Address *</label><input id="email" name="Email" type="email" required className={field} data-testid="input-email" /></div>
                  <div><label className={labelCls} htmlFor="phone">Phone Number *</label><input id="phone" name="Phone" type="tel" required className={field} data-testid="input-phone" /></div>
                  <div><label className={labelCls} htmlFor="address">Property Address *</label><input id="address" name="Property Address" type="text" required className={field} data-testid="input-address" /></div>
                  <div><label className={labelCls} htmlFor="city">City *</label><input id="city" name="City" type="text" required className={field} data-testid="input-city" /></div>
                  <div><label className={labelCls} htmlFor="zip">ZIP Code *</label><input id="zip" name="ZIP Code" type="text" required className={field} data-testid="input-zip" /></div>
                  <div>
                    <label className={labelCls} htmlFor="ptype">Project Type *</label>
                    <select id="ptype" name="Project Type" required className={selectCls} data-testid="select-ptype" defaultValue="">
                      <option value="" disabled>Select...</option>
                      <option>Residential</option><option>Commercial</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="service">Service Needed *</label>
                    <select id="service" name="Service Needed" required className={selectCls} data-testid="select-service" defaultValue="">
                      <option value="" disabled>Select...</option>
                      {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="desc">Project Description *</label>
                  <textarea id="desc" name="Project Description" required rows={5} className={field} data-testid="input-description" placeholder="Please describe your project, any damage, preferred finish or texture, and any additional information that will help us prepare your estimate." />
                </div>

                <div className="pt-2">
                  <p className="label-accent text-zinc-500 mb-6">Optional Details</p>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls} htmlFor="contactmethod">Preferred Contact Method</label>
                      <select id="contactmethod" name="Preferred Contact Method" className={selectCls} defaultValue=""><option value="">No preference</option><option>Phone</option><option>Text Message</option><option>Email</option></select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="besttime">Best Time to Contact</label>
                      <select id="besttime" name="Best Time to Contact" className={selectCls} defaultValue=""><option value="">No preference</option><option>Morning</option><option>Afternoon</option><option>Evening</option></select>
                    </div>
                    <div><label className={labelCls} htmlFor="size">Approximate Project Size</label><input id="size" name="Approximate Project Size" type="text" className={field} placeholder="e.g. 2,000 sq ft" /></div>
                    <div>
                      <label className={labelCls} htmlFor="timeline">Project Timeline</label>
                      <select id="timeline" name="Project Timeline" className={selectCls} defaultValue=""><option value="">Select...</option><option>ASAP</option><option>Within 30 Days</option><option>1–3 Months</option><option>Just Looking</option></select>
                    </div>
                    <div><label className={labelCls} htmlFor="budget">Budget Range</label><input id="budget" name="Budget Range" type="text" className={field} placeholder="e.g. $2,000 - $5,000" /></div>
                    <div>
                      <label className={labelCls} htmlFor="hear">How Did You Hear About Us?</label>
                      <select id="hear" name="How Did You Hear About Us" className={selectCls} defaultValue=""><option value="">Select...</option><option>Google Search</option><option>Google Maps</option><option>Facebook</option><option>Instagram</option><option>Referral</option><option>Previous Customer</option><option>Yard Sign</option><option>Other</option></select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="photos">Upload Photos (optional)</label>
                  <div className="border border-dashed border-zinc-300 p-6 text-center bg-zinc-50 hover:border-adn-red transition-colors">
                    <input id="photos" type="file" accept="image/*" multiple onChange={handleFileChange} className="w-full text-sm text-zinc-600" data-testid="input-photos" />
                    <p className="text-xs text-zinc-400 mt-2">Attach photos of your project or damage to help us prepare your estimate.</p>
                    {uploading && <p className="text-sm text-adn-red mt-3 flex items-center justify-center gap-2" data-testid="upload-status"><Loader2 size={16} className="animate-spin" /> Uploading photos...</p>}
                    {uploadError && <p className="text-sm text-adn-red mt-3" data-testid="upload-error">{uploadError}</p>}
                    {photos.length > 0 && (
                      <ul className="mt-3 space-y-1 text-left" data-testid="uploaded-list">
                        {photos.map((p, i) => (
                          <li key={i} className="text-sm text-zinc-600 flex items-center gap-2"><Check size={14} className="text-adn-red" /> {p.filename}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <input type="hidden" name="Project Photos" value={photos.map((p) => p.url).join(", ")} />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="Consent" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-5 h-5 accent-adn-red" data-testid="input-consent" />
                  <span className="text-sm text-zinc-600">I agree to be contacted by A.D.N STUCCO INC regarding my estimate request. *</span>
                </label>

                <button type="submit" disabled={!consent || uploading} data-testid="submit-estimate" className={`w-full text-lg font-bold uppercase tracking-wide py-5 transition-colors duration-300 ${consent && !uploading ? "bg-adn-red text-white hover:bg-adn-charcoal cursor-pointer" : "bg-zinc-300 text-zinc-500 cursor-not-allowed"}`}>Request My Free Estimate</button>
                <p className="text-center text-sm text-zinc-500">Your information is secure and will never be shared with third parties.</p>
              </form>
            </div>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-6">
            <div className="bg-adn-charcoal text-white p-8">
              <h3 className="text-xl font-heading font-bold mb-6">Contact Information</h3>
              <ul className="space-y-5 text-sm">
                <li><a href={BUSINESS.phoneHref} data-testid="contact-phone" className="flex items-start gap-3 hover:text-adn-red transition-colors"><Phone size={20} className="text-adn-red mt-0.5" /><span><span className="block text-zinc-400 text-xs uppercase tracking-wider">Call Us</span>{BUSINESS.phone}</span></a></li>
                <li><a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-start gap-3 hover:text-adn-red transition-colors break-all"><Mail size={20} className="text-adn-red mt-0.5" /><span><span className="block text-zinc-400 text-xs uppercase tracking-wider">Email</span>{BUSINESS.email}</span></a></li>
                <li className="flex items-start gap-3"><Clock size={20} className="text-adn-red mt-0.5" /><span><span className="block text-zinc-400 text-xs uppercase tracking-wider">Business Hours</span>{BUSINESS.hours}</span></li>
                <li className="flex items-start gap-3"><MapPin size={20} className="text-adn-red mt-0.5" /><span><span className="block text-zinc-400 text-xs uppercase tracking-wider">Service Areas</span>{CITIES.slice(0, 6).map((c) => c.name).join(", ")} &amp; more</span></li>
              </ul>
              <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
                <a href={BUSINESS.social.facebook} target="_blank" rel="noreferrer" className="p-2 border border-white/20 hover:border-adn-red transition-colors"><Facebook size={18} /></a>
                <a href={BUSINESS.social.instagram} target="_blank" rel="noreferrer" className="p-2 border border-white/20 hover:border-adn-red transition-colors"><Instagram size={18} /></a>
              </div>
            </div>

            <div className="bg-adn-off border border-zinc-200 p-8">
              <div className="flex items-center gap-2"><StarRating /><span className="font-bold">5.0</span></div>
              <p className="text-sm text-zinc-600 mt-3">Rated 5 stars by homeowners &amp; businesses across Central Florida on Google.</p>
              <a href={BUSINESS.social.google} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-adn-red font-semibold text-sm mt-4"><Star size={16} className="fill-adn-red" /> Read our Google Reviews</a>
            </div>

            <div className="bg-white border border-zinc-200 p-8">
              <h3 className="text-lg font-heading font-bold mb-4">Why Choose A.D.N</h3>
              <ul className="space-y-3">
                {TRUST_BADGES.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm text-zinc-700 font-medium"><ShieldCheck size={18} className="text-adn-red" /> {b}</li>
                ))}
              </ul>
            </div>

            <div className="border border-zinc-200 overflow-hidden">
              <iframe title="A.D.N Stucco service map" src={BUSINESS.mapEmbed} width="100%" height="280" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
